import { useState, useEffect, useRef } from 'react';
import { loadAssetPrices } from '../api/assetPricesClient';
import { loadFredSeries } from '../api/fredClient';
import type { OHLCVPoint } from '../api/types';
import type { SelectedAsset } from '../components/AssetSearchOverlay';

export interface AssetOverlayData {
    ticker: string;
    assetType: string;
    name: string;
    data: OHLCVPoint[];
    loading: boolean;
}

const ASSET_COLORS: [string, string][] = [
    ['#a8ff37', '#2e7d32'],
    ['#38bdf8', '#1565c0'],
];

export function getAssetColor(index: number, isDark: boolean = true): string {
    const pair = ASSET_COLORS[index % ASSET_COLORS.length];
    return isDark ? pair[0] : pair[1];
}

type ResampleFreq = '1min' | '5min' | '15min' | '1hour' | '4hour' | '12hour' | '1day';

// UI-friendly ticker → Tiingo/backend symbol. The backend's FX_COMMODITIES
// table keys off short ISO codes (XAU/XAG/XPT/XPD), but displaying "XAU" in a
// tooltip isn't obvious to most users. Keep the display ticker intuitive and
// translate at the fetch boundary.
const BACKEND_TICKER_ALIAS: Record<string, string> = {
    GOLD: 'XAU',
};

// Tiingo's earliest available observation per backend symbol. Requests whose
// entire chunk range falls before this date get a 400 upstream ("End date
// must be >= ...") which surfaces as a 500 to the client and fails the whole
// series via Promise.all. Clamp the fetch window so every chunk lands in a
// range Tiingo actually serves.
const BACKEND_EARLIEST_ISO: Record<string, string> = {
    XAU: '2020-01-02',
};

const TIINGO_FREQS: { minutes: number; freq: ResampleFreq }[] = [
    { minutes: 1, freq: '1min' },
    { minutes: 5, freq: '5min' },
    { minutes: 15, freq: '15min' },
    { minutes: 60, freq: '1hour' },
    { minutes: 240, freq: '4hour' },
    { minutes: 720, freq: '12hour' },
    { minutes: 1440, freq: '1day' },
];

function resolutionToFreq(resolutionMinutes: number): { freq: ResampleFreq; freqMinutes: number } {
    for (let i = TIINGO_FREQS.length - 1; i >= 0; i--) {
        const { minutes, freq } = TIINGO_FREQS[i];
        if (minutes <= resolutionMinutes && resolutionMinutes % minutes === 0) {
            return { freq, freqMinutes: minutes };
        }
    }
    return { freq: '1min', freqMinutes: 1 };
}

function aggregateOHLCV(points: OHLCVPoint[], targetMinutes: number, fetchMinutes: number): OHLCVPoint[] {
    if (targetMinutes <= fetchMinutes || points.length === 0) return points;

    const bucketMs = targetMinutes * 60_000;
    const buckets = new Map<number, OHLCVPoint[]>();

    for (const p of points) {
        const key = Math.floor(p.t.getTime() / bucketMs) * bucketMs;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push(p);
    }

    const result: OHLCVPoint[] = [];
    for (const [, group] of [...buckets.entries()].sort((a, b) => a[0] - b[0])) {
        const last = group[group.length - 1];
        result.push({
            t: last.t,
            open: group[0].open,
            high: Math.max(...group.map(p => p.high)),
            low: Math.min(...group.map(p => p.low)),
            close: last.close,
            vol: group.reduce((sum, p) => sum + p.vol, 0),
        });
    }
    return result;
}

export function useAssetPriceOverlay(
    selectedAssets: SelectedAsset[],
    startDate: Date | null,
    endDate: Date | null,
    resolutionMinutes: number = 1440,
    displayResolutionMinutes?: number,
): AssetOverlayData[] {
    const [entries, setEntries] = useState<AssetOverlayData[]>([]);
    const callIdRef = useRef(0);

    const { freq: frequency, freqMinutes } = resolutionToFreq(resolutionMinutes);
    const effectiveDisplayMinutes = displayResolutionMinutes ?? resolutionMinutes;

    useEffect(() => {
        if (selectedAssets.length === 0 || !startDate || !endDate) {
            setEntries([]);
            return;
        }

        const callId = ++callIdRef.current;
        const pending = selectedAssets.map((a) => ({
            ticker: a.ticker,
            assetType: a.assetType,
            name: a.name,
            data: [] as OHLCVPoint[],
            loading: true,
        }));
        setEntries([...pending]);

        const fetchStart = new Date(startDate);
        fetchStart.setUTCHours(0, 0, 0, 0);

        selectedAssets.forEach((asset) => {
            // FRED macro series (oil spot, rates, etc.) skip the Tiingo path
            // entirely - different source, different schema, daily-only so no
            // chunking or sub-daily frequency selection applies.
            const backendTicker = BACKEND_TICKER_ALIAS[asset.ticker] ?? asset.ticker;
            const earliestIso = BACKEND_EARLIEST_ISO[backendTicker];
            const effectiveStart = earliestIso && new Date(earliestIso + 'T00:00:00Z').getTime() > fetchStart.getTime()
                ? new Date(earliestIso + 'T00:00:00Z')
                : fetchStart;
            const fetcher = asset.assetType === 'macro'
                ? loadFredSeries(backendTicker, effectiveStart, endDate)
                : loadAssetPrices(backendTicker, asset.assetType, effectiveStart, endDate, {
                    frequency,
                    aggregate: frequency === '1day' ? 'daily' : 'none',
                });

            fetcher
                .then((points) => {
                    if (callIdRef.current !== callId) return;
                    const aligned = aggregateOHLCV(points, effectiveDisplayMinutes, freqMinutes);
                    setEntries((prev) =>
                        prev.map((e) =>
                            e.ticker === asset.ticker
                                ? { ...e, data: aligned, loading: false }
                                : e,
                        ),
                    );
                })
                .catch(() => {
                    if (callIdRef.current !== callId) return;
                    setEntries((prev) =>
                        prev.map((e) =>
                            e.ticker === asset.ticker
                                ? { ...e, data: [], loading: false }
                                : e,
                        ),
                    );
                });
        });

        return () => {
            callIdRef.current++;
        };
    }, [
        selectedAssets.map((a) => a.ticker).join(','),
        startDate?.getTime(),
        endDate?.getTime(),
        frequency,
        resolutionMinutes,
        effectiveDisplayMinutes,
    ]);

    return entries;
}